import { Controller } from '@hotwired/stimulus';
import { CR_INLINE_FIELD_EVENTS } from './cr_inline_field_protocol.js';

/**
 * Next Meeting Suggestions Controller (retour Tripode n°4, variante 4B)
 *
 * One-click « prochaine réunion » date suggestions on the CR draft (spec § 6.4).
 * The controller never PATCHes anything itself: it writes the suggested ISO date
 * (then the "HH:MM" time, when one is known) into the calendar's HIDDEN fields and
 * fires a bubbling `change` on each, so the existing per-field save
 * (`cr-inline-field#patchField`) and the calendar resync (`date-picker`
 * `#onHiddenChange` / `#onTimeHiddenChange`) run exactly as for a manual pick.
 *
 * Contract with `cr-inline-field` (pinned by
 * `test/javascript/controllers/next_meeting_suggestions_controller.test.mjs`):
 * - snapshot first (S11): a bubbling `focusin` on BOTH hidden fields while they are
 *   still empty, so `#captureCommittedValue` records the empty committed value that
 *   a failed save restores;
 * - same URL as a manual edit: a suggestion is a write like any other, the last
 *   date chosen wins (the D38 `if_blank` guard was removed, QA Charles 2026-10-08).
 *   `patchField` reads the queue key and the saved message SYNCHRONOUSLY at
 *   dispatch, so both overrides are put back right after: a later manual edit
 *   never inherits them;
 * - one serial queue: both writes share a `queue` param, so the time PATCH starts
 *   only once the date PATCH settled. The shared status line therefore reaches its
 *   final « saved » state from the LAST write, which carries the announcement;
 * - announcement (S6): the last written field carries
 *   `data-cr-inline-field-saved-message-param` during its `change`. The status line
 *   is never written directly (`cr-inline-field` clears it on every state).
 *
 * Failure (S11, diffusion won the race, validation, transport): on
 * `cr-inline-field:failed` (dispatched on `window`) for a field
 * written by a pick still in flight, the hidden fields still in flight are emptied
 * (a field whose PATCH already succeeded keeps its saved value). When the date was
 * among them, the visible date is emptied too and the suggestions come back; a date
 * saved before a refused time stays on screen. No `change` is fired: it would re-run
 * `patchField` and send an empty PATCH over a date set meanwhile.
 *
 * Targets:
 * - date: hidden ISO field `next_meeting[date]` (carries the `cr-inline-field` wiring)
 * - time: hidden "HH:MM" field `next_meeting[time]` (idem)
 * - display: the visible date input (`date-picker` display target), focused after a pick
 * - suggestions: the wrapper of the intro and buttons, hidden after a pick
 *
 * Actions:
 * - pick: writes the clicked suggestion (params `date` ISO, optional `time` "HH:MM")
 *
 * @example
 * <div data-controller="next-meeting-suggestions">
 *   <input type="text" data-next-meeting-suggestions-target="display">
 *   <input type="hidden" name="next_meeting[date]"
 *          data-next-meeting-suggestions-target="date"
 *          data-action="change->cr-inline-field#patchField"
 *          data-cr-inline-field-url-param="/…/prochaine-reunion">
 *   <input type="hidden" name="next_meeting[time]" data-next-meeting-suggestions-target="time" …>
 *   <div data-next-meeting-suggestions-target="suggestions">
 *     <button type="button"
 *             data-action="click->next-meeting-suggestions#pick"
 *             data-next-meeting-suggestions-date-param="2026-10-14"
 *             data-next-meeting-suggestions-time-param="09:00">mer. 14/10 · 09h00</button>
 *   </div>
 * </div>
 */

// Shared `cr-inline-field` queue key for the two writes of one pick.
const SUGGESTION_QUEUE = 'next-meeting-suggestion';

export default class extends Controller {
  static targets = ['date', 'time', 'display', 'suggestions'];

  connect() {
    // Hidden fields written by the pick whose PATCH has not settled yet.
    this.inFlight = new Set();
    this.onFailed = this.#onFailed.bind(this);
    this.onSucceeded = this.#onSucceeded.bind(this);
    window.addEventListener(CR_INLINE_FIELD_EVENTS.failed, this.onFailed);
    window.addEventListener(CR_INLINE_FIELD_EVENTS.succeeded, this.onSucceeded);
  }

  disconnect() {
    window.removeEventListener(CR_INLINE_FIELD_EVENTS.failed, this.onFailed);
    window.removeEventListener(CR_INLINE_FIELD_EVENTS.succeeded, this.onSucceeded);
    this.inFlight?.clear();
  }

  /**
   * Writes the clicked suggestion into the hidden fields (date, then time).
   *
   * @param {Event & { params: { date?: string, time?: string } }} event
   */
  pick(event) {
    event.preventDefault?.();
    const { date, time } = event.params || {};
    if (!date || !this.hasDateTarget) return;

    const writes = [[this.dateTarget, String(date)]];
    if (time && this.hasTimeTarget) writes.push([this.timeTarget, String(time)]);

    // 1. Snapshot BEFORE any write (S11), on both hidden fields.
    for (const field of [this.dateTarget, this.hasTimeTarget ? this.timeTarget : null]) {
      field?.dispatchEvent(new Event('focusin', { bubbles: true }));
    }

    const message = this.#savedMessage(event);
    writes.forEach(([field, value], index) => {
      this.inFlight.add(field);
      this.#write(field, value, index === writes.length - 1 ? message : null);
    });

    if (this.hasSuggestionsTarget) this.suggestionsTarget.hidden = true;
    this.#focusDisplay();
  }

  /**
   * One write: the `cr-inline-field` queue and saved-message params are overridden
   * only for the synchronous duration of the `change` dispatch, then put back.
   *
   * @param {HTMLInputElement} field
   * @param {string} value
   * @param {string|null} savedMessage
   */
  #write(field, value, savedMessage) {
    const restore = {
      crInlineFieldQueueParam: field.dataset.crInlineFieldQueueParam,
      crInlineFieldSavedMessageParam: field.dataset.crInlineFieldSavedMessageParam,
    };

    field.dataset.crInlineFieldQueueParam = SUGGESTION_QUEUE;
    if (savedMessage) field.dataset.crInlineFieldSavedMessageParam = savedMessage;

    try {
      field.value = value;
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      for (const [key, previous] of Object.entries(restore)) {
        if (previous === undefined) delete field.dataset[key];
        else field.dataset[key] = previous;
      }
    }
  }

  /**
   * Announcement read by the status line once the last write is saved (S6).
   *
   * @param {Event} event
   * @returns {string}
   */
  #savedMessage(event) {
    const label = (event.currentTarget?.textContent || event.target?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    return label ? `Prochaine réunion enregistrée : ${label}` : 'Prochaine réunion enregistrée';
  }

  /**
   * Hands the focus back to the visible date field without opening its calendar:
   * VanillaCalendar (`openOnFocus`, default true) opens its popup from a `focus`
   * listener on that very input. A one-shot CAPTURE listener on this element stops
   * that `focus` before it reaches the input. `focus` is dispatched synchronously
   * by `focus()`, so the listener is removed right after. `focusin` (a separate
   * event, which `cr-inline-field` and `date-picker` listen to) is untouched.
   */
  #focusDisplay() {
    if (!this.hasDisplayTarget) return;

    const display = this.displayTarget;
    const swallow = (event) => {
      if (event.target === display) event.stopPropagation();
    };

    this.element.addEventListener('focus', swallow, true);
    try {
      display.focus();
    } finally {
      this.element.removeEventListener('focus', swallow, true);
    }
  }

  /**
   * A failed suggestion PATCH (422, transport): only the fields still
   * in flight go back to empty, never one whose PATCH already succeeded. The
   * visible date and the suggestions come back only when the DATE itself was not
   * saved: a date saved before a refused time stays on screen, as in the DB.
   * Never a `change` (see header).
   *
   * @param {CustomEvent} event
   */
  #onFailed(event) {
    const field = event.detail?.field;
    if (!field || !this.inFlight.has(field)) return;

    const dateUnsaved = this.hasDateTarget && this.inFlight.has(this.dateTarget);
    const pending = [...this.inFlight];
    this.inFlight.clear();
    for (const control of pending) control.value = '';
    if (!dateUnsaved) return;

    if (this.hasDisplayTarget) this.displayTarget.value = '';
    if (this.hasSuggestionsTarget) this.suggestionsTarget.hidden = false;
  }

  /**
   * A settled suggestion PATCH: the field leaves the in-flight set, so a later
   * failure of a MANUAL edit of the same field never brings the suggestions back.
   *
   * @param {CustomEvent} event
   */
  #onSucceeded(event) {
    const field = event.detail?.field;
    if (field) this.inFlight.delete(field);
  }
}
